// Leitura do retorno do CliSiTef gravado em tefTerminal.response.message.
//
// O plugin onii-capacitor-plugin-clisitef devolve "cancel: true" em toda venda
// que nao deu certo, por isso transacoes que falharam (negadas pelo autorizador,
// sem comunicacao com o SiTef, erro no pinpad...) ficam com status "cancelled".
// O codigo e a mensagem do erro so existem nesse texto, que chega assim:
//   "[51] NAO AUTORIZADA" (plugin atual: texto do emissor/adquirente)
//   "[51] Negado pelo autorizador." (versoes antigas do plugin)
//   "[-120] -120" (codigo fora da tabela: o plugin repete o numero)
//   "Erro [ConfiguraIntSiTefInterativo]: [-12] Erro desconhecido"
//   "Erro [ConfiguraIntSiTefInterativo]: -12" (versoes antigas do plugin)
//   "Erro na venda: <detalhe>" (mensagens do plugin, sem codigo)
//
// Quando a mensagem vem sem descricao, ela e preenchida pelas tabelas da
// especificacao "SiTef - Interface Simplificada com a aplicacao" (versao 243).
//
// Copia de onii-admin (src/utils/tefResponse.js). Os relatorios usam as copias
// CommonJS em onii-background-tasks (utils/tef-response.js) e onii-backend
// (utils/onii/common/tef-response.js). Mantenha as copias em sincronia.

// Tabela 5.2.1 - codigos de retorno das funcoes de transacao
const RETURN_CODE_DESCRIPTIONS = {
  "-1": "Módulo não inicializado. O PDV tentou chamar alguma rotina sem antes executar a função configura.",
  "-2": "Operação cancelada pelo operador.",
  "-3": "O parâmetro função / modalidade é inexistente/inválido.",
  "-4": "Falta de memória no PDV.",
  "-5": "Sem comunicação com o SiTef.",
  "-6": "Operação cancelada pelo usuário (no pinpad).",
  "-8": "A CliSiTef não possui a implementação da função necessária, provavelmente está desatualizada.",
  "-9": "A automação chamou a rotina ContinuaFuncaoSiTefInterativo sem antes iniciar uma função iterativa.",
  "-10": "Algum parâmetro obrigatório não foi passado pela automação comercial.",
  "-12": "Erro na execução da rotina iterativa. Provavelmente o processo iterativo anterior não foi executado até o final.",
  "-13": "Documento fiscal não encontrado nos registros da CliSiTef.",
  "-15": "Operação cancelada pela automação comercial.",
  "-20": "Parâmetro inválido passado para a função.",
  "-21": "Utilizada uma palavra proibida para coletar dados em aberto no pinpad.",
  "-25": "Erro no Correspondente Bancário: deve realizar sangria.",
  "-30": "Erro de acesso ao arquivo.",
  "-40": "Transação negada pelo servidor SiTef.",
  "-41": "Dados inválidos.",
  "-43": "Problema na execução de alguma das rotinas no pinpad.",
  "-50": "Transação não segura.",
  "-100": "Erro interno do módulo."
};

// Tabela 2 - codigos de retorno das funcoes de configuracao (ConfiguraIntSiTefInterativo)
const CONFIG_CODE_DESCRIPTIONS = {
  "1": "Endereço IP inválido ou não resolvido.",
  "2": "Código da loja inválido.",
  "3": "Código de terminal inválido.",
  "6": "Erro na inicialização do Tcp/Ip.",
  "7": "Falta de memória.",
  "8": "Não encontrou a CliSiTef ou ela está com problemas.",
  "9": "Configuração de servidores SiTef foi excedida.",
  "10": "Erro de acesso na pasta CliSiTef.",
  "11": "Dados inválidos passados pela automação.",
  "12": "Modo seguro não ativo.",
  "13": "Caminho DLL inválido."
};

// Codigos positivos ("outro valor positivo" da tabela 5.2.1) sao a resposta do
// emissor/adquirente, que a especificacao nao descreve e que muda entre redes
// (o 43 de uma rede e "nao permitida", nao "cartao roubado"). O texto dessa
// resposta chega no comando 22 da CliSiTef, que o plugin grava no log como
// "Mensagem: <texto>"; versoes antigas do plugin so gravam a negativa generica.
// Esses retornos saem com fromAuthorizer: true, para a tela indicar a origem.
const LOG_MESSAGE = /^Mensagem:\s*/;

const CONFIG_PREFIX = /^\s*Erro \[ConfiguraIntSiTefInterativo\]:\s*/i;
const BRACKET_CODE = /\[(-?\d+)\]\s*/;
const TRAILING_CODE = /:?\s*(-?\d+)\s*$/;
const UNKNOWN_DESCRIPTION = /^erro desconhecido\.?$/i;
const GENERIC_DENIAL = /^negad[ao] pelo autorizador\.?$/i;

// Mensagens do comando 22 no log, sem quebras de linha e repeticoes
// ("51-NAO AUTORIZADA" costuma vir duas vezes)
function getAuthorizerLogMessage(log) {
  if (!Array.isArray(log)) {
    return "";
  }

  const messages = [];

  log.forEach(line => {
    if (typeof line !== "string" || !LOG_MESSAGE.test(line)) return;

    // Caracteres de controle (ex.: \u001a no fim da linha) viram espaco
    const text = line
      .replace(LOG_MESSAGE, "")
      .split("")
      .map(char => {
        const charCode = char.charCodeAt(0);
        return charCode < 32 || charCode === 127 ? " " : char;
      })
      .join("")
      .replace(/\s+/g, " ")
      .trim();

    if (text && messages.indexOf(text) === -1) {
      messages.push(text);
    }
  });

  return messages.join(" - ");
}

// Descricao pela tabela quando a mensagem nao traz uma
function describeCode(code, isConfig, log) {
  const number = Number(code);

  if (isConfig && number > 0) {
    return CONFIG_CODE_DESCRIPTIONS[code] || "Erro desconhecido.";
  }

  if (RETURN_CODE_DESCRIPTIONS[code]) {
    return RETURN_CODE_DESCRIPTIONS[code];
  }

  // "outro valor positivo" / "outro valor negativo" da tabela 5.2.1
  if (number > 0) {
    return getAuthorizerLogMessage(log) || "Transação negada.";
  }

  return "Erro detectado internamente pela CliSiTef.";
}

// log: tefTerminal.response.log, usado para o texto do emissor/adquirente
export function parseTefMessage(message, log) {
  if (typeof message !== "string" || !message.trim()) {
    return null;
  }

  const raw = message.trim();
  const isConfig = CONFIG_PREFIX.test(raw);
  let text = raw.replace(CONFIG_PREFIX, "");
  let code = null;

  const bracketMatch = text.match(BRACKET_CODE);

  if (bracketMatch) {
    code = bracketMatch[1];
    text = text.replace(BRACKET_CODE, "").trim();
  } else {
    const trailingMatch = text.match(TRAILING_CODE);

    if (trailingMatch && (isConfig || trailingMatch.index === 0)) {
      code = trailingMatch[1];
      text = text.slice(0, trailingMatch.index).trim();
    }
  }

  if (code === null) {
    return { code: null, message: raw, fromAuthorizer: false };
  }

  // Sem descricao, so o numero repetido ("[-120] -120"), "Erro desconhecido"
  // ou a negativa generica do plugin ("[51] Negado pelo autorizador.")
  if (
    !text ||
    text === code ||
    UNKNOWN_DESCRIPTION.test(text) ||
    GENERIC_DENIAL.test(text)
  ) {
    text = describeCode(code, isConfig, log);
  }

  return {
    code,
    message: isConfig ? `Erro na configuração do SiTef: ${text}` : text,
    fromAuthorizer: !isConfig && Number(code) > 0
  };
}

const TEF_PROVIDERS = ["tef", "tef-gsurf"];

// Cancelamentos intencionais, nao sao falha: -2 operador/app (tambem apos 60 s
// sem cartao), -6 cliente no pinpad, -15 automacao comercial.
const INTENTIONAL_CANCEL_CODES = ["-2", "-6", "-15"];

// Codigo e mensagem de uma venda TEF que falhou. Retorna null para vendas
// aprovadas, ainda abertas (a mensagem e so o ultimo passo do pinpad), de
// outros provedores, sem retorno ou canceladas de proposito.
export function getTransactionTefFailure(transaction) {
  const details = (transaction && transaction.details) || {};

  if (
    ["success", "open"].indexOf(details.status) !== -1 ||
    TEF_PROVIDERS.indexOf(details.provider) === -1
  ) {
    return null;
  }

  const response = transaction.tefTerminal && transaction.tefTerminal.response;
  const tefReturn = response
    ? parseTefMessage(response.message, response.log)
    : null;

  if (!tefReturn || INTENTIONAL_CANCEL_CODES.indexOf(tefReturn.code) !== -1) {
    return null;
  }

  return tefReturn;
}
